import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Stack, TextField, Chip, Alert, CircularProgress,
  Paper, Tooltip, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  ToggleButtonGroup, ToggleButton, Divider, InputAdornment,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { format, subDays } from 'date-fns';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { matchesImpressionsApi, saleFilterCounterfactualApi } from '../../services/api';
import { NetworkHeroKPIs } from './index';
import { formatCurrency } from '../../utils/formatters';
import PerformanceTabBar from './PerformanceTabBar';
import usePageLoadingEffect from '../../hooks/usePageLoadingEffect';

const PRESETS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

// Funnel hero cards (MEASURED — HARD numbers, bold/primary).
// Cut-off card (spec overlay/match-display-cap-3.md §4.4, D4): matches trimmed by the per-size
// display cap on OPENED overlays (same unit as Overlays shown; the raw log is computation grain,
// ~60x larger). The cap is read from the data (cutoff_caps, oldest first), never hard-coded.
function cutoffCard(card, funnel) {
  const caps = funnel.cutoff_caps || [];
  const cap = caps.length ? caps[caps.length - 1] : null;
  const changed = caps.length > 1 && funnel.cutoff_cap_changed_at
    ? new Date(funnel.cutoff_cap_changed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    : null;
  const label = changed ? `Trimmed by cap (${caps.join(' → ')}, ${changed})`
    : cap ? `Trimmed by ${cap}-match cap` : 'Trimmed by match cap';
  const shown = funnel.overlays_shown || 0;
  const pct = shown ? ((100 * (funnel.cutoff_overlays_opened || 0)) / shown).toFixed(1) : '0.0';
  return {
    ...card,
    label,
    subKey: 'cutoff_overlays_opened',
    subLabel: `overlays (${pct}% of shown)`,
    tooltip: `Partner matches that passed every filter but were not shown, because a size shows at most ${cap || 'N'} partners`
      + (changed ? ` (the cap changed from ${caps[0]} to ${cap} on ${changed})` : '')
      + '. Counted on overlays shoppers opened — the same unit as Overlays shown; the raw log also counts prefetches.'
      + ' A steady share is expected; a sudden rise means many sizes now have more partners than the cap.',
  };
}

const FUNNEL_CARDS = [
  { key: 'overlays_shown', label: 'Overlays shown', color: '#1976d2',
    format: (v) => (v || 0).toLocaleString(),
    tooltip: 'The shopper selected a sold-out size and the overlay rendered (Size Selected) — the honest "matches served" moment. NOT impressions (those fire on match computation/prefetch). Populates as size-selected beacon data accumulates.' },
  { key: 'sale_filter_drops', label: 'Sale-filter drops', color: '#ed6c02',
    format: (v) => (v || 0).toLocaleString(),
    tooltip: 'Matches hidden by retailer sale-policy toggles over the window.' },
  { key: 'cutoff_drops_opened', color: '#9c27b0', format: (v) => (v || 0).toLocaleString() },  // label/tooltip: cutoffCard()
  { key: 'clicks', label: 'Clicks', color: '#0288d1',
    format: (v) => (v || 0).toLocaleString(),
    tooltip: 'Clicks routed through the network in this window (live from handoff_cookies — reconciles with the Clicks tab).' },
  { key: 'approved_orders', label: 'Orders', color: '#2e7d32',
    format: (v) => (v || 0).toLocaleString(),
    tooltip: 'Approved orders (by order date) in this window.' },
];

const PROJECTION_LENSES = [
  { key: 'handoff_net', label: 'Handoff net (2%)' },
  { key: 'network_gmv', label: 'Network GMV' },
  { key: 'retailer_commission', label: 'Retailer commission' },
];

// Visual wall for the MODELED projection panel — distinct background/border (honesty rule B).
const PROJECTION_SX = {
  p: 2.5, mb: 4, borderRadius: 2,
  bgcolor: 'rgba(156, 39, 176, 0.04)',
  border: '1px solid rgba(156, 39, 176, 0.3)',
};

export default function MatchesImpressionsDashboard() {
  const today = format(new Date(), 'yyyy-MM-dd');
  const thirtyDaysAgo = format(subDays(new Date(), 30), 'yyyy-MM-dd');

  const [fromDate, setFromDate] = useState(thirtyDaysAgo);
  const [toDate, setToDate] = useState(today);
  const [summary, setSummary] = useState(null);
  const [counterfactual, setCounterfactual] = useState(null);
  const [projection, setProjection] = useState(null);
  const [potentialTrend, setPotentialTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageLoadingEffect(loading);
  const [error, setError] = useState(null);
  const [lens, setLens] = useState('handoff_net');
  const [tableView, setTableView] = useState('measured'); // 'measured' | 'estimated'

  // 'Add retailer X' lever
  const [brandsText, setBrandsText] = useState('');
  const [catalogueSize, setCatalogueSize] = useState('');
  const [overlap, setOverlap] = useState(null); // {matched_count,total,overlap_ratio,...}

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [s, p, t, c] = await Promise.all([
        matchesImpressionsApi.getSummary({ from_date: fromDate, to_date: toDate }),
        matchesImpressionsApi.getProjection({ from_date: fromDate, to_date: toDate }),
        matchesImpressionsApi.getPotentialTrend({ days: 30 }),
        saleFilterCounterfactualApi.getNetwork({ from_date: fromDate, to_date: toDate }),
      ]);
      if (s.success) setSummary(s.data); else setError(s.error || 'Failed to load summary');
      if (p.success) setProjection(p.data);
      if (t.success) setPotentialTrend(t.data);
      if (c.success) setCounterfactual(c.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const applyPreset = (days) => {
    setFromDate(format(subDays(new Date(), days), 'yyyy-MM-dd'));
    setToDate(format(new Date(), 'yyyy-MM-dd'));
  };

  // Debounced brand-overlap lookup as the user types brands.
  useEffect(() => {
    const brands = brandsText.split(/[\n,]+/).map((b) => b.trim()).filter(Boolean);
    if (!brands.length) { setOverlap(null); return; }
    const t = setTimeout(async () => {
      try {
        const res = await matchesImpressionsApi.getBrandOverlap(brands);
        if (res.success) setOverlap(res.data);
      } catch { /* non-blocking */ }
    }, 500);
    return () => clearTimeout(t);
  }, [brandsText]);

  const fetchLever = useCallback(async () => {
    const size = parseInt(catalogueSize, 10);
    if (!size || !overlap) return;
    const res = await matchesImpressionsApi.getProjection({
      from_date: fromDate, to_date: toDate,
      new_retailer_catalogue_size: size,
      new_retailer_brand_overlap: overlap.overlap_ratio,
    });
    if (res.success) setProjection(res.data);
  }, [catalogueSize, overlap, fromDate, toDate]);

  if (loading && !summary) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  const funnel = summary?.funnel || {};
  const addressable = summary?.addressable || {};
  const emptiedRateBySource = summary?.emptied_rate_by_source || {};

  // Group rows by source retailer: sources ordered by their total impressions (busiest first),
  // partners within each source ordered by impressions desc. The source cell is blanked on
  // repeat rows in render so each source's partners cluster visually.
  const byPair = (() => {
    const rows = summary?.by_pair || [];
    const sourceTotals = {};
    rows.forEach((r) => { sourceTotals[r.source_retailer_id] = (sourceTotals[r.source_retailer_id] || 0) + (r.impressions || 0); });
    return [...rows].sort((a, b) => {
      if (a.source_retailer_id !== b.source_retailer_id) {
        // Different source → order by the source's total impressions, then name for stability.
        const d = (sourceTotals[b.source_retailer_id] || 0) - (sourceTotals[a.source_retailer_id] || 0);
        return d !== 0 ? d : a.source_retailer_id.localeCompare(b.source_retailer_id);
      }
      // Same source → partner impressions desc.
      return (b.impressions || 0) - (a.impressions || 0);
    });
  })();

  // Funnel bar data (measured flows).
  const funnelBars = [
    { stage: 'Overlays', value: funnel.overlays_shown || 0, color: '#1976d2' },
    { stage: 'Sale-drops', value: funnel.sale_filter_drops || 0, color: '#ed6c02' },
    { stage: 'Clicks', value: funnel.clicks || 0, color: '#0288d1' },
    { stage: 'Orders', value: funnel.approved_orders || 0, color: '#2e7d32' },
  ];

  const lensData = projection?.monthly_projection?.[lens];
  const rates = projection?.rates || {};
  const vpo = projection?.value_per_order_90d || {};
  const newRetailer = projection?.new_retailer;
  const trend = potentialTrend?.trend || [];

  const basisLine = projection ? (
    `overlay→click ${rates.overlay_to_click_7d != null ? (rates.overlay_to_click_7d * 100).toFixed(2) + '%' : '—'} (7d) · `
    + `click→order ${rates.click_to_order_7d != null ? (rates.click_to_order_7d * 100).toFixed(2) + '%' : '—'} (7d) · `
    + `value/order from ${vpo.orders || 0} orders (90d)`
  ) : '';

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Matches &amp; impressions
      </Typography>
      <PerformanceTabBar />

      {/* Shared date controls (drive both sections) */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Stack direction="row" spacing={1}>
          <TextField label="From" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} size="small" />
          <TextField label="To" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} size="small" />
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {PRESETS.map((p) => (
            <Chip key={p.days} label={p.label} size="small" onClick={() => applyPreset(p.days)}
              variant={fromDate === format(subDays(new Date(), p.days), 'yyyy-MM-dd') ? 'filled' : 'outlined'} />
          ))}
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ===================== SECTION 1 — PROJECTION (MODELED, walled off) ===================== */}
      <Paper elevation={0} sx={PROJECTION_SX}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="h6" fontWeight="bold" color="secondary">Revenue projection</Typography>
          <Chip label="Projected — based on trailing actuals + assumptions" size="small" color="secondary" variant="outlined" />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Modeled estimates — always a range, never a promise. {basisLine}
        </Typography>

        <ToggleButtonGroup size="small" exclusive value={lens} onChange={(e, v) => v && setLens(v)} sx={{ mb: 2 }}>
          {PROJECTION_LENSES.map((l) => <ToggleButton key={l.key} value={l.key}>{l.label}</ToggleButton>)}
        </ToggleButtonGroup>

        {lensData && rates.overlay_to_click_7d != null ? (
          <Box sx={{ mb: 1 }}>
            <Typography variant="h4" color="secondary" fontWeight="bold">
              {formatCurrency(lensData.low)} – {formatCurrency(lensData.high)}
              <Typography component="span" variant="body2" color="text.secondary"> / month (estimated)</Typography>
            </Typography>
          </Box>
        ) : (
          <Alert severity="info" sx={{ mb: 1 }}>
            Accumulating data — the projection bases on <strong>overlays shown</strong> (size-selected), which begins collecting once the updated script is live. It will populate over the next several days; until then there isn&apos;t a 7-day overlay→click window to project from.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* 'Add retailer X' what-if lever */}
        <Typography variant="subtitle2" gutterBottom>What-if: add a retailer</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
          <TextField
            label="Prospect brands (comma or newline separated)"
            multiline minRows={2} fullWidth size="small"
            value={brandsText} onChange={(e) => setBrandsText(e.target.value)}
            helperText={overlap ? `${overlap.matched_count} of ${overlap.total} recognized in network · ${overlap.total - overlap.matched_count} new (overlap ${(overlap.overlap_ratio * 100).toFixed(0)}%)` : 'Type brand names to see network overlap'}
          />
          <TextField
            label="Catalogue size" type="number" size="small"
            value={catalogueSize} onChange={(e) => setCatalogueSize(e.target.value)}
            onBlur={fetchLever}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">items</InputAdornment> } }}
            sx={{ minWidth: 160 }}
          />
        </Stack>
        {newRetailer && (
          <Typography variant="body2" sx={{ mt: 1.5 }} color="text.secondary">
            Est. incremental GMV:{' '}
            <Box component="span" color="secondary.main" fontWeight="bold">
              {formatCurrency(newRetailer.est_incremental_gmv_low)} – {formatCurrency(newRetailer.est_incremental_gmv_high)}/mo
            </Box>{' '}
            <Tooltip title={newRetailer.basis}><InfoOutlinedIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /></Tooltip>
            {' '}· {newRetailer.catalogue_size} items × {(newRetailer.brand_overlap * 100).toFixed(0)}% overlap → eff. match rate {(newRetailer.effective_match_rate * 100).toFixed(1)}%
          </Typography>
        )}
      </Paper>

      {/* ===================== SECTION 2 — NETWORK STATE (MEASURED) ===================== */}
      <Typography variant="h6" fontWeight="bold" gutterBottom>Network state</Typography>

      {/* Zone A — addressable overlap (RINGFENCED level — own-history trend only) */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2">Addressable overlap</Typography>
          <Tooltip title="A stock-state level, counted in SIZE OPPORTUNITIES — not products. One product with 3 sold-out sizes at 2 partners contributes 6. Both retailers must be active. Its only honest comparison is its OWN history (is network overlap growing?). NOT comparable to the funnel flows below, and never ratio'd against drops.">
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </Tooltip>
        </Stack>
        {/* Labelled "size opportunities", NOT "potential matches": at ~3x the product count it
            invited direct comparison with the product-grain Live figure on the Pipeline tab,
            making two views of ONE join read as rival numbers. */}
        <Typography variant="h4" fontWeight="bold">
          {(addressable.potential_matches || 0).toLocaleString()}
          <Typography component="span" variant="body2" color="text.secondary"> size opportunities{addressable.snapshot_date ? ` (as of ${addressable.snapshot_date})` : ''}</Typography>
        </Typography>
        {/* The same join has two sides and both are real. Showing them together is the point:
            demand is what the network needs covered, supply is what it can cover right now. */}
        {(addressable.source_products_oos || addressable.partner_products_live) && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Across{' '}
            <strong>{(addressable.source_products_oos || 0).toLocaleString()}</strong> products
            needing cover (demand) and{' '}
            <strong>{(addressable.partner_products_live || 0).toLocaleString()}</strong> products
            able to cover them (supply).{' '}
            <Typography component="span" variant="caption">
              Both pair-grain: a product matched to several partners counts once per partner, so
              these are larger than the network distinct counts on Pipeline → Trends.
            </Typography>
          </Typography>
        )}
        {trend.length > 1 ? (
          <>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={trend} margin={{ top: 12, right: 16, bottom: 4, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="snapshot_date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <RTooltip formatter={(v) => v.toLocaleString()} />
              <Line type="monotone" dataKey="potential_matches" stroke="#1976d2" strokeWidth={2} dot={{ r: 3 }} name="Potential matches" />
            </LineChart>
          </ResponsiveContainer>
          {/* The series changes basis on 2026-08-19: snapshots before that date were captured
              with a spurious similarity >= 0.85 floor that discarded 17% of cross-retailer
              IDENTICAL edges (mostly shared-barcode gtin_direct matches). Earlier points are
              therefore biased LOW and the step is a definition change, not network growth. */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Points before 2026-08-19 are not comparable to later ones: they were captured with
            a similarity filter that under-counted by ~7%, and without the active-retailer
            filter that now excludes prospects (~16% over-count). The two biases do not cancel
            and neither is recoverable. Compare within, not across, that date.
          </Typography>
          </>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Trend builds daily — history starts {potentialTrend?.history_starts || '2026-06-19'}.
          </Typography>
        )}
      </Paper>

      {/* Zone B — realized funnel (FLOWS) */}
      <Typography variant="subtitle2" gutterBottom>Realized funnel <Typography component="span" variant="caption" color="text.secondary">(over the selected window)</Typography></Typography>
      <Box sx={{ mb: 2 }}>
        <NetworkHeroKPIs data={funnel} cards={FUNNEL_CARDS.map((c) => (c.key === 'cutoff_drops_opened' ? cutoffCard(c, funnel) : c))} />
      </Box>
      {(funnel.emptied_carousels || 0) > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {funnel.emptied_carousels.toLocaleString()} carousels emptied by the sale filter (distinct calls) — see the Emptied column and per-retailer rate in the table below.
        </Typography>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={funnelBars} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="stage" />
            <YAxis />
            <RTooltip formatter={(v) => v.toLocaleString()} />
            <Bar dataKey="value">
              {funnelBars.map((d) => <Cell key={d.stage} fill={d.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Paper>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Overlays shown is the honest &quot;matches served&quot; node (size-selected) — it populates as beacon data accumulates. Clicks are live (reconcile with the Clicks tab).
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
        Other upstream filtering (price-divergence veto, partner stock, status, similarity threshold) is not itemized here — not currently instrumented. Impressions (matches computed) are intentionally excluded — they over-count vs overlays actually shown.
      </Typography>

      {/* Per source→partner detail — toggle between Measured and Estimated-impact views */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2">By retailer pair</Typography>
        <ToggleButtonGroup size="small" exclusive value={tableView} onChange={(e, v) => v && setTableView(v)}>
          <ToggleButton value="measured">Measured</ToggleButton>
          <ToggleButton value="estimated">Estimated impact</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {tableView === 'estimated' && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            <Chip label="Estimated" size="small" color="secondary" variant="outlined" sx={{ mr: 1, height: 18 }} />
            Counterfactual model — the same one behind Badge Fairness &quot;Adj. NGS&quot;: each
            retailer&apos;s hidden appearances × their own click rate (clicks ÷ appearances shown,
            this window). Headline = single-flip marginal: only calls the retailer&apos;s own
            toggle recovers. Receive counts every hidden appearance; send counts emptied
            carousels only (a click on a surviving partner still pays the source). Point
            estimates, not promises.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Receive side — what each retailer&apos;s own filter costs them</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'rgba(156, 39, 176, 0.03)', mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Retailer</TableCell>
                  <TableCell align="right"><Tooltip title="Distinct overlay calls where this retailer was hidden."><span>Hidden calls</span></Tooltip></TableCell>
                  <TableCell align="right"><Tooltip title="Hidden calls their own toggle-off alone would recover (single-flip marginal)."><span>Own-recoverable</span></Tooltip></TableCell>
                  <TableCell align="right"><Tooltip title="Est. missed clicks from the general sale filter (full-price hides on-sale partner offers)."><span>Clicks (sale filter)</span></Tooltip></TableCell>
                  <TableCell align="right"><Tooltip title="Est. missed clicks from the deeper-discount filter (both on sale, partner deeper by > threshold)."><span>Clicks (deeper)</span></Tooltip></TableCell>
                  <TableCell align="right"><Tooltip title="Est. missed orders from the general sale filter."><span>Orders (sale filter)</span></Tooltip></TableCell>
                  <TableCell align="right"><Tooltip title="Est. missed orders from the deeper-discount filter."><span>Orders (deeper)</span></Tooltip></TableCell>
                  <TableCell align="right"><Tooltip title="Est. missed GMV from the general sale filter."><span>GMV (sale filter)</span></Tooltip></TableCell>
                  <TableCell align="right"><Tooltip title="Est. missed GMV from the deeper-discount filter."><span>GMV (deeper)</span></Tooltip></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(counterfactual?.retailers || []).filter((r) => r.receive?.suppressed_calls > 0).map((r) => {
                  // Split each cost into general sale filter (base) vs deeper discount. The
                  // two are mutually exclusive per call and sum to the total, so
                  // base = total − deeper. deeper is null on days before the split backfill.
                  const split = (total, deeper) => (total == null ? [null, null] : [total - (deeper || 0), deeper]);
                  const [clicksBase, clicksDeeper] = split(r.receive.est_missed_clicks, r.receive.est_deeper_only_missed_clicks);
                  const [ordersBase, ordersDeeper] = split(r.receive.est_missed_orders, r.receive.est_deeper_only_missed_orders);
                  const [gmvBase, gmvDeeper] = split(r.receive.est_missed_gmv_eur, r.receive.est_deeper_only_missed_gmv_eur);
                  const numCell = (v) => (v != null ? Math.round(v).toLocaleString() : '—');
                  const eurCell = (v) => (v != null ? formatCurrency(v) : '—');
                  return (
                    <TableRow key={`cf-recv-${r.retailer_id}`}>
                      <TableCell>{r.retailer_id}</TableCell>
                      <TableCell align="right">{r.receive.suppressed_calls.toLocaleString()}</TableCell>
                      <TableCell align="right">{r.receive.own_recoverable_calls.toLocaleString()}</TableCell>
                      <TableCell align="right">{numCell(clicksBase)}</TableCell>
                      <TableCell align="right">{numCell(clicksDeeper)}</TableCell>
                      <TableCell align="right">{numCell(ordersBase)}</TableCell>
                      <TableCell align="right">{numCell(ordersDeeper)}</TableCell>
                      <TableCell align="right">{eurCell(gmvBase)}</TableCell>
                      <TableCell align="right">{eurCell(gmvDeeper)}</TableCell>
                    </TableRow>
                  );
                })}
                {!(counterfactual?.retailers || []).some((r) => r.receive?.suppressed_calls > 0) && (
                  <TableRow><TableCell colSpan={9}><Typography variant="body2" color="text.secondary">No counterfactual data in this window (rollup starts 2026-06-17).</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Send side — emptied carousels only</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'rgba(156, 39, 176, 0.03)' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Retailer</TableCell>
                  <TableCell align="right">Emptied calls</TableCell>
                  <TableCell align="right">Own-recoverable</TableCell>
                  <TableCell align="right"><Tooltip title="Est. missed sends from the general sale filter."><span>Sends (sale filter)</span></Tooltip></TableCell>
                  <TableCell align="right"><Tooltip title="Est. missed sends from the deeper-discount filter."><span>Sends (deeper)</span></Tooltip></TableCell>
                  <TableCell align="right"><Tooltip title="Est. missed commission from the general sale filter."><span>Commission (sale filter)</span></Tooltip></TableCell>
                  <TableCell align="right"><Tooltip title="Est. missed commission from the deeper-discount filter."><span>Commission (deeper)</span></Tooltip></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(counterfactual?.retailers || []).filter((r) => r.send?.suppressed_calls > 0).map((r) => {
                  const split = (total, deeper) => (total == null ? [null, null] : [total - (deeper || 0), deeper]);
                  const [sendsBase, sendsDeeper] = split(r.send.est_missed_clicks, r.send.est_deeper_only_missed_clicks);
                  const [commBase, commDeeper] = split(r.send.est_missed_commission_eur, r.send.est_deeper_only_missed_commission_eur);
                  const numCell = (v) => (v != null ? Math.round(v).toLocaleString() : '—');
                  const eurCell = (v) => (v != null ? formatCurrency(v) : '—');
                  return (
                    <TableRow key={`cf-send-${r.retailer_id}`}>
                      <TableCell>{r.retailer_id}</TableCell>
                      <TableCell align="right">{r.send.suppressed_calls.toLocaleString()}</TableCell>
                      <TableCell align="right">{r.send.own_recoverable_calls.toLocaleString()}</TableCell>
                      <TableCell align="right">{numCell(sendsBase)}</TableCell>
                      <TableCell align="right">{numCell(sendsDeeper)}</TableCell>
                      <TableCell align="right">{eurCell(commBase)}</TableCell>
                      <TableCell align="right">{eurCell(commDeeper)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      {tableView === 'measured' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Potential impressions on the source site → what each side&apos;s sale filter hid → overlays actually shown → clicks. Over the window. (Overlays shown has data from 2026-06-22.)
        </Typography>
      )}

      {tableView === 'measured' && (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Source</TableCell>
              <TableCell>Partner</TableCell>
              <TableCell align="right"><Tooltip title="Match-cards computed for this partner on the source's sold-out sizes — the full potential before filtering."><span>Potential impressions</span></Tooltip></TableCell>
              <TableCell align="right"><Tooltip title="The source retailer's own sale filter hid this partner."><span>Hidden by source</span></Tooltip></TableCell>
              <TableCell align="right"><Tooltip title="The partner's own sale filter hid itself from this source (reciprocity)."><span>Hidden by partner</span></Tooltip></TableCell>
              <TableCell align="right"><Tooltip title="Beacon-confirmed overlays the shopper opened that contained this partner (data from 2026-06-22)."><span>Overlays shown</span></Tooltip></TableCell>
              <TableCell align="right"><Tooltip title="Clicks on this partner's match (live, reconciles with the Clicks tab)."><span>Clicks</span></Tooltip></TableCell>
              <TableCell align="right"><Tooltip title="Distinct calls where this pair was dropped AND the whole carousel ended empty (shopper saw nothing). Per-call fact — NOT summable across a source's pairs; the summable total + rate is on the source's header row."><span>Emptied*</span></Tooltip></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {byPair.length === 0 && (
              <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">No data in this window.</Typography></TableCell></TableRow>
            )}
            {byPair.map((r, i) => {
              const isGroupStart = i === 0 || byPair[i - 1].source_retailer_id !== r.source_retailer_id;
              const groupSx = isGroupStart && i !== 0 ? { '& td': { borderTop: '2px solid', borderTopColor: 'divider' } } : undefined;
              return (
                <TableRow key={`${r.source_retailer_id}→${r.partner_retailer_id}`} sx={groupSx}>
                  <TableCell sx={{ fontWeight: isGroupStart ? 600 : 400 }}>
                    {isGroupStart ? r.source_retailer_id : ''}
                    {isGroupStart && (emptiedRateBySource[r.source_retailer_id]?.emptied_calls || 0) > 0 && (() => {
                      const e = emptiedRateBySource[r.source_retailer_id];
                      return (
                        <Tooltip title={`${e.emptied_calls.toLocaleString()} of ${e.filter_calls.toLocaleString()} sale-filter calls ended with an empty carousel (distinct calls).`}>
                          <Typography component="span" variant="caption" sx={{ display: 'block', fontWeight: 400, color: e.emptied_rate >= 0.5 ? 'error.main' : 'text.secondary' }}>
                            {(e.emptied_rate * 100).toFixed(0)}% emptied
                          </Typography>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  <TableCell>{r.partner_retailer_id}</TableCell>
                  <TableCell align="right">{(r.impressions || 0).toLocaleString()}</TableCell>
                  <TableCell align="right">{(r.hidden_by_source || 0).toLocaleString()}</TableCell>
                  <TableCell align="right">{(r.hidden_by_partner || 0).toLocaleString()}</TableCell>
                  <TableCell align="right">{(r.overlays || 0).toLocaleString()}</TableCell>
                  <TableCell align="right">{(r.clicks || 0).toLocaleString()}</TableCell>
                  <TableCell align="right">{(r.emptied_calls || 0) > 0 ? r.emptied_calls.toLocaleString() : '—'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      )}
      {tableView === 'measured' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          * Emptied = distinct calls where this pair was dropped and the carousel ended empty. A call
          dropping two partners and ending empty counts under both pairs, so the Emptied column is
          NOT summable — the de-duplicated total and rate are on each source&apos;s header row
          (&quot;% emptied&quot;). Partner-policy drops are the reciprocal case (the partner&apos;s
          sale setting emptied this retailer&apos;s carousel).
        </Typography>
      )}
    </Box>
  );
}
